import { useCallback, useEffect, useMemo, useRef } from 'react'

import { AddLineItemDocument, BillingAddonReferenceFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'

import useCreateSubscriptionOverload from 'modules/discover/hooks/useCreateSubscriptionOverload'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'

type DatoModuleUpsellProps = {
  billingAddon: BillingAddonReferenceFragment | null
  moduleName: string
  origin: string
  featureId?: string
  subscriptionExpirationDate?: string
  chargebeeAddonId?: string
}

const useButtonActions = ({
  billingAddon,
  moduleName,
  origin,
  featureId,
  subscriptionExpirationDate,
  chargebeeAddonId,
}: DatoModuleUpsellProps) => {
  const { company } = useCurrentSession()
  const [addLineItem] = useMutation(AddLineItemDocument, {
    extractorFn: (data) => data.upsellMutations.createUpsellAddLineItem,
  })
  const { currentSeats, period, configPlan, allAddons, setConfigPlan, togglePlanSelect } =
    useSelfUpsellContext()

  const addon = useMemo(() => {
    return (
      allAddons.f25Addons.find((a) => a.internalTitle === billingAddon?.internalTitle) ||
      allAddons.f25Addons[0]
    )
  }, [allAddons.f25Addons, billingAddon?.internalTitle])

  const initializedRef = useRef(false)

  useEffect(() => {
    if (addon && !initializedRef.current) {
      initializedRef.current = true
      if (!addon.isSelected) togglePlanSelect(addon)
      setConfigPlan({
        addon: [addon.pricing.getPlanId(period) || ''],
        variant: addon.activeVariantType,
      })
    }
  }, [addon])

  const addLineItemMutation = useCallback(async () => {
    return await addLineItem({
      variables: {
        newAddons: Array.isArray(configPlan.addon)
          ? configPlan.addon.map((addonItem) => ({
              itemPriceId: addonItem || '',
              seats: currentSeats,
              module: moduleName,
            }))
          : [
              {
                itemPriceId: configPlan.addon || '',
                seats: currentSeats,
                module: moduleName,
              },
            ],
        origin: origin,
        feature: featureId || '',
      },
    })
  }, [configPlan, currentSeats, moduleName, addLineItem])

  const createSubscriptionOverload = useCreateSubscriptionOverload([company.id])

  const handleSubscriptionOverloadMutation = async () => {
    const result = await createSubscriptionOverload({
      chargebeeId: chargebeeAddonId || '',
      overloadReason: 'landing_page',
      newExpirationDate: subscriptionExpirationDate || '',
    })

    return result
  }

  return { addLineItemMutation, handleSubscriptionOverloadMutation }
}

export default useButtonActions
